import React, { useMemo, useState } from 'react'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { setQrCodeData, setQrCodeModal } from 'Manage/actions'
import InviteTeammates from 'Manage/components/AddTeamMembersModal/components/InviteTeammates'
import { trackUploadFileCount } from 'utils/sendy'
import { showUpgradeCardModal, showHtmlAnalysisModal, UPGRADE_QR_PAYLOAD } from 'actions'
import { copyToClipboard, onUpgradePlan, isEligibleForAIEdit, openAIEditor } from '../../../utils/general'
import { PLANS } from 'constants/plans'
import * as Sentry from '@sentry/react'
import SuccessIcon from '../../SuccessIcon/SuccessIcon'

import styles from './SuccessBox.module.css'
import ShareDropdown from '../../ShareDropdown/ShareDropdown'
import ReferralSection from '../../ReferralSection/ReferralSection'
import celloService from '../../../services/cello'

const ImmediateSuccessBox = ({ userProfile, site, files, onClose, onReset }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [showCopyTooltip, setShowCopyTooltip] = useState(false)
  const isAIEligible = useMemo(() => isEligibleForAIEdit(userProfile, site), [userProfile, site])

  if (!site || !site.subdomain) return null

  const { subdomain } = site
  const isFreeAccount = !userProfile.productId
  const isB2BUser = userProfile?.userCategory === 'B2B'
  const linkStatus = site?.linkStatus?.toLowerCase()
  const isLive = linkStatus === 'online'
  const isError = linkStatus === 'error'
  const isProcessing = !isLive && !isError

  const handleClose = () => {
    onClose()
    trackUploadFileCount()
  }

  const onQRCodeClick = () => {
    if (userProfile.productId) {
      dispatch(setQrCodeModal(true))
      dispatch(setQrCodeData(site))
    } else {
      dispatch(
        showUpgradeCardModal({
          ...UPGRADE_QR_PAYLOAD,
          onClick: () => onUpgradePlan({ planId: PLANS.TINY.id, userProfile }, dispatch)
        })
      )
    }
  }

  const onEditAIClick = (e) => {
    e.preventDefault()
    openAIEditor(subdomain, !userProfile.productId)
  }

  const onCustomiseClick = (e) => {
    e.preventDefault()
    history.push(`/manage/update?site=${encodeURIComponent(subdomain)}&edit_link_name=true`)
    onReset(site, files)
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://${subdomain.replace(/\/$/, '')}`)
      setShowCopyTooltip(true)
      setTimeout(() => setShowCopyTooltip(false), 2000)
    } catch (err) {
      // Fallback for older browsers
      copyToClipboard(`https://${subdomain.replace(/\/$/, '')}`)
      setShowCopyTooltip(true)
      setTimeout(() => setShowCopyTooltip(false), 2000)
    }
  }

  const handleSendInvites = async (emails) => {
    try {
      alert(`Invites sent successfully to ${emails.length} email(s)!`)
    } catch (error) {
      console.error('Failed to send invites:', error)
      alert('Failed to send invites. Please try again.')
      throw error
    }
  }

  const handleGenerateReferralLink = async () => {
    if (!userProfile) return
    try {
      await celloService.initAndOpen(userProfile)
    } catch (error) {
      console.error('Failed to generate referral link:', error)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.modalRow}>
        {/* Main Content */}
        <div className={styles.mainColumn}>
          <div className={styles.successBox}>
            <button className={styles.closeButton} onClick={handleClose}>
              <img src="/delete.png" alt="quit" width="16" height="16" />
            </button>
            <div className={styles.centeredContent}>
              <h1 className={styles.successTitle}>Success!</h1>
              <div className="flex w-100">
                <div className={styles.successIcon}>
                  <SuccessIcon width={40} height={40} />
                </div>
                <div className="text-center">
                  <p className={styles.projectLiveText}>Your project is now live</p>

                  {/* View Link */}
                  <div className={styles.domainContainer}>
                    <a
                      className={styles.domainLink}
                      href={`https://${subdomain.replace(/\/$/, '')}${isFreeAccount ? '?mode=suggestions' : ''}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="SuccessBoxHomePage-a-domain"
                    >
                      {subdomain}
                    </a>
                  </div>
                  {/* View Link */}
                </div>
              </div>

              <div className="d-flex flex-column mt-2 mb-3 align-items-center w-100">
                {/* Main Action Buttons */}
                <div className={styles.actionButtons}>
                  {files?.length > 0 && !isProcessing && (
                    <button className={styles.actionButton} onClick={() => onReset(site, files)}>
                      Update
                    </button>
                  )}
                  <Button
                    className="flex-grow-1"
                    onClick={() =>
                      window.open(
                        `https://${subdomain.replace(/\/$/, '')}${isFreeAccount ? '?mode=suggestions' : ''}`,
                        '_blank'
                      )
                    }
                  >
                    View link
                  </Button>
                </div>
                {/* Main Action Buttons */}

                {/* Secondary Action Buttons */}
                <div className={`mt-3 ${styles.secondaryActionButtons}`}>
                  {isAIEligible ? (
                    <button className={`${styles.actionButton} ${styles.actionButtonSlim}`} onClick={onEditAIClick}>
                      <img className="me-2" height="20" width="20" src="/assets/icons/edit.svg" alt="Edit with AI" />
                      Edit with AI
                    </button>
                  ) : (
                    <button className={`${styles.actionButton} ${styles.actionButtonSlim}`} onClick={onCustomiseClick}>
                      <img className="me-2" height="20" width="20" src="/assets/icons/edit.svg" alt="Customize" />
                      Customize
                    </button>
                  )}
                  <button className={`${styles.actionButton} ${styles.actionButtonSlim}`} onClick={onQRCodeClick}>
                    <img className="me-2" height="20" width="20" src="/assets/icons/qr-code.svg" alt="QR Code" />
                    QR Code
                  </button>
                  {!isFreeAccount && (
                    <ShareDropdown
                      url={`https://${subdomain.replace(/\/$/, '')}${isFreeAccount ? '?mode=suggestions' : ''}`}
                      trial={isFreeAccount}
                      className={`${styles.actionButton} ${styles.actionButtonSlim}`}
                      onClick={() => onReset(site, files)}
                    >
                      Share
                    </ShareDropdown>
                  )}
                  {isFreeAccount && (
                    <OverlayTrigger
                      placement="bottom"
                      show={showCopyTooltip}
                      overlay={<Tooltip id="copy-tooltip">Link copied!</Tooltip>}
                    >
                      <button className={`${styles.actionButton} ${styles.actionButtonSlim}`} onClick={handleCopyLink}>
                        <img className="me-2" height="20" width="20" src="/assets/icons/copy-link.svg" alt="Copy" />
                        Copy link
                      </button>
                    </OverlayTrigger>
                  )}
                </div>
                {/* Secondary Action Buttons */}

                <div>
                  {(isB2BUser || !isFreeAccount) && (
                    <InviteTeammates onSendInvites={handleSendInvites} userProfile={userProfile} />
                  )}
                </div>

                {!isB2BUser && isFreeAccount && (
                  <ReferralSection userProfile={userProfile} onGenerateLink={handleGenerateReferralLink} />
                )}
                <div className={styles.helpSection}>
                  <span>Something broken? - </span>
                  <a
                    className={styles.helpLink}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      dispatch(showHtmlAnalysisModal(site))
                    }}
                  >
                    Analyze issues
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImmediateSuccessBox
